import { Tag } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNarrativeTextInput } from '../hooks/useNarrativeTextInput';
import { useCurrentNode, useNarrativeState, useUpdateNode } from '../store/hooks';
import { VariantText } from '../types/narrative';
import { getWorldFromNodeId } from '../utils/narrativeHelpers';

import { VariantEditorModal } from './VariantEditorModal';

export const FinalSequenceEditor = React.memo(function FinalSequenceEditor() {
  // Use Zustand hooks
  const narrativeState = useNarrativeState();
  const currentNode = useCurrentNode();
  const updateNode = useUpdateNode();
  const currentNodeId = narrativeState.currentNodeId;

  // Modal state
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);

  // Get Node2 choices to display
  const node2 = narrativeState.nodes['S1_Player_Declaration'];
  const node2Choices = useMemo(() => node2?.choices || [], [node2]);

  // Determine which world we're in based on the node ID
  const worldFromNodeId = getWorldFromNodeId(currentNodeId);
  const isGoodWorld = worldFromNodeId === 'good';
  const worldKey = isGoodWorld ? 'goodWorld' : 'badWorld';

  // Initialize variant content if not present
  useEffect(() => {
    if (!currentNode?.variantContent?.[worldKey] && node2Choices.length > 0) {
      const initialVariants: VariantText[] = node2Choices.map((choice) => ({
        choiceId: choice.id,
        choiceText: choice.text,
        variantLine: '',
      }));

      updateNode(currentNodeId, {
        variantContent: {
          ...currentNode?.variantContent,
          [worldKey]: {
            variants: initialVariants,
            placeholderPosition: 0, // Always at the start
          },
        },
      });
    }
  }, [currentNode?.variantContent, worldKey, node2Choices, currentNodeId, updateNode]);

  // Handler for text field updates
  const handleTextBeforeUpdate = useCallback(
    (value: string) => {
      const fieldName = isGoodWorld ? 'textBeforeGoodWorld' : 'textBeforeBadWorld';
      updateNode(currentNodeId, { [fieldName]: value });
    },
    [currentNodeId, updateNode, isGoodWorld]
  );

  const handleTextAfterUpdate = useCallback(
    (value: string) => {
      const fieldName = isGoodWorld ? 'textAfterGoodWorld' : 'textAfterBadWorld';
      updateNode(currentNodeId, { [fieldName]: value });
    },
    [currentNodeId, updateNode, isGoodWorld]
  );

  // Create debounced inputs for text before and after
  const textBeforeValue = isGoodWorld
    ? currentNode?.textBeforeGoodWorld
    : currentNode?.textBeforeBadWorld;
  const textAfterValue = isGoodWorld
    ? currentNode?.textAfterGoodWorld
    : currentNode?.textAfterBadWorld;

  const textBeforeInput = useNarrativeTextInput(textBeforeValue || '', handleTextBeforeUpdate);
  const textAfterInput = useNarrativeTextInput(textAfterValue || '', handleTextAfterUpdate);

  // Also maintain the combined text field for compatibility
  useEffect(() => {
    const textBefore = textBeforeValue || '';
    const textAfter = textAfterValue || '';
    const combinedText =
      textBefore + (textBefore && textAfter ? ' [VARIANT] ' : '[VARIANT]') + textAfter;

    if (currentNode && currentNode.text !== combinedText) {
      updateNode(currentNodeId, { text: combinedText });
    }
  }, [textBeforeValue, textAfterValue, currentNode, currentNodeId, updateNode]);

  // Check if text fields need highlighting
  const shouldHighlightTextFields = useMemo(() => {
    if (!currentNode) return false;

    const textBeforeEmpty = !textBeforeValue || textBeforeValue.trim() === '';
    const textAfterEmpty = !textAfterValue || textAfterValue.trim() === '';

    if (!textBeforeEmpty && !textAfterEmpty) return false;

    // Check if all variant lines are filled
    const currentWorldVariants = currentNode.variantContent?.[worldKey]?.variants || [];
    const allVariantsFilled =
      currentWorldVariants.length > 0 &&
      currentWorldVariants.every((v) => v.variantLine && v.variantLine.trim() !== '');

    // Check if all choices are filled
    const allChoicesFilled =
      currentNode.choices?.every((choice) => choice.text && choice.text.trim() !== '') ?? false;

    return allVariantsFilled && allChoicesFilled && (currentNode.choices?.length ?? 0) > 0;
  }, [currentNode, worldKey, textBeforeValue, textAfterValue]);

  // Safety check - must be after hooks
  if (!currentNode) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div
        className={`border border-[#334155] rounded-lg p-4 ${isGoodWorld ? 'bg-green-950/20' : 'bg-red-950/20'}`}
      >
        <h3 className={`font-semibold mb-2 ${isGoodWorld ? 'text-green-400' : 'text-red-400'}`}>
          Variable Content System - {isGoodWorld ? 'Better World' : 'Worse World'}
        </h3>

        {/* Node2 Preview Card */}
        <div className="bg-[#0a0f1f] border border-[#1a2d4d]/50 rounded-lg overflow-hidden mb-4">
          <div className="bg-[#0a0f1f] px-3 py-2 border-b border-[#1a2d4d]/50">
            <h4 className="text-xs font-medium text-gray-400">
              Just prior to the B-Story diversion, the player had these choices:
            </h4>
          </div>

          <div className="p-3">
            {node2?.text && (
              <div className="mb-2">
                <div className="text-gray-300 leading-relaxed text-xs bg-[#050914] p-2 rounded border border-[#1a2d4d]/30">
                  {node2.text.length > 150 ? `${node2.text.substring(0, 150)}...` : node2.text}
                </div>
              </div>
            )}

            {node2Choices.length > 0 && node2Choices.some((choice) => choice.text.trim()) && (
              <div className="space-y-1">
                {node2Choices.map(
                  (choice, index) =>
                    choice.text.trim() && (
                      <div
                        key={choice.id}
                        className="bg-[#1a2d2f] border border-[#059669]/30 text-gray-300 rounded px-2 py-1 text-xs"
                      >
                        <span className="text-gray-400">Choice {index + 1}:</span>{' '}
                        {choice.text.length > 60
                          ? `${choice.text.substring(0, 60)}...`
                          : choice.text}
                      </div>
                    )
                )}
              </div>
            )}

            {!node2?.text && !node2Choices?.some((choice) => choice.text.trim()) && (
              <p className="text-xs text-gray-500 italic text-center py-4">
                No content yet in Node 2
              </p>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-300">
          The NPC will recall the player&apos;s earlier choice and frame it with a{' '}
          <span
            className={isGoodWorld ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}
          >
            {isGoodWorld ? 'hopeful/optimistic' : 'bitter/pessimistic'}
          </span>{' '}
          perspective in this {isGoodWorld ? 'Better' : 'Worse'} World.
        </div>
      </div>

      {/* Main Editor */}
      <div
        className={`border rounded-lg p-4 ${
          isGoodWorld ? 'bg-green-950/10 border-green-600' : 'bg-red-950/10 border-red-600'
        }`}
      >
        {/* Text Before Field */}
        <div className="mb-4">
          <label
            className={`text-sm transition-all duration-300 ${
              shouldHighlightTextFields && (!textBeforeValue || textBeforeValue.trim() === '')
                ? `animate-pulse ${isGoodWorld ? 'text-green-300' : 'text-red-300'}`
                : `${isGoodWorld ? 'text-green-400' : 'text-red-400'}`
            }`}
          >
            {shouldHighlightTextFields &&
              (!textBeforeValue || textBeforeValue.trim() === '') &&
              '⚠️ '}
            {`Text before the NPC's variant line:`}
          </label>
          <textarea
            value={textBeforeInput.value}
            onChange={(e) => textBeforeInput.onChange(e.target.value)}
            placeholder={`Text that appears before the variant in the ${isGoodWorld ? 'Better' : 'Worse'} world...`}
            className={`w-full mt-1 border rounded-md p-2 text-white focus:outline-none text-sm resize-none ${
              isGoodWorld
                ? 'bg-green-950/20 border-green-600 focus:border-green-400'
                : 'bg-red-950/20 border-red-600 focus:border-red-400'
            } ${
              shouldHighlightTextFields && (!textBeforeValue || textBeforeValue.trim() === '')
                ? 'border-yellow-400 shadow-lg shadow-yellow-400/20 animate-pulse'
                : ''
            }`}
            rows={3}
          />
        </div>

        {/* Variant Editor Button */}
        <div className="mb-4">
          <button
            onClick={() => setIsVariantModalOpen(true)}
            className={`w-full px-4 py-3 font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${
              (currentNode.variantContent?.[worldKey]?.variants?.length ?? 0) > 0 &&
              currentNode.variantContent?.[worldKey]?.variants?.every((v) => v.variantLine?.trim())
                ? 'bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] text-white hover:shadow-lg hover:scale-105'
                : 'bg-gradient-to-r from-gray-600 to-gray-700 text-gray-300 animate-pulse hover:from-gray-500 hover:to-gray-600'
            }`}
          >
            <Tag className="w-4 h-4" />
            <span>
              {(currentNode.variantContent?.[worldKey]?.variants?.length ?? 0) > 0 &&
              currentNode.variantContent?.[worldKey]?.variants?.every((v) => v.variantLine?.trim())
                ? 'Edit Variant Lines'
                : 'Define Variant Lines'}
            </span>
          </button>
          <p className="text-xs text-gray-400 mt-1 text-center">
            Click to define how the NPC references Node 2 choices
          </p>
        </div>

        {/* Text After Field */}
        <div className="mb-4">
          <label
            className={`text-sm transition-all duration-300 ${
              shouldHighlightTextFields && (!textAfterValue || textAfterValue.trim() === '')
                ? `animate-pulse ${isGoodWorld ? 'text-green-300' : 'text-red-300'}`
                : `${isGoodWorld ? 'text-green-400' : 'text-red-400'}`
            }`}
          >
            {shouldHighlightTextFields &&
              (!textAfterValue || textAfterValue.trim() === '') &&
              '⚠️ '}
            {`Text after the NPC's variant line:`}
          </label>
          <textarea
            value={textAfterInput.value}
            onChange={(e) => textAfterInput.onChange(e.target.value)}
            placeholder={`Text that appears after the variant in the ${isGoodWorld ? 'Better' : 'Worse'} world...`}
            className={`w-full mt-1 border rounded-md p-2 text-white focus:outline-none text-sm resize-none ${
              isGoodWorld
                ? 'bg-green-950/20 border-green-600 focus:border-green-400'
                : 'bg-red-950/20 border-red-600 focus:border-red-400'
            } ${
              shouldHighlightTextFields && (!textAfterValue || textAfterValue.trim() === '')
                ? 'border-yellow-400 shadow-lg shadow-yellow-400/20 animate-pulse'
                : ''
            }`}
            rows={3}
          />
        </div>

        {/* Choices */}
        <div className="space-y-2">
          <label className={`text-sm ${isGoodWorld ? 'text-green-400' : 'text-red-400'}`}>
            Choices:
          </label>
          {currentNode.choices?.map((choice, idx) => (
            <input
              key={idx}
              type="text"
              value={choice.text}
              onChange={(e) => {
                const newChoices = [...(currentNode.choices || [])];
                newChoices[idx] = { ...newChoices[idx], text: e.target.value };
                updateNode(currentNodeId, {
                  choices: newChoices,
                });
              }}
              placeholder={`${isGoodWorld ? 'Better' : 'Worse'} world choice ${idx + 1}...`}
              className={`w-full border rounded-md p-2 text-white focus:outline-none text-sm ${
                isGoodWorld
                  ? 'bg-green-950/20 border-green-600 focus:border-green-400'
                  : 'bg-red-950/20 border-red-600 focus:border-red-400'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Variant Editor Modal */}
      <VariantEditorModal
        isOpen={isVariantModalOpen}
        onClose={() => setIsVariantModalOpen(false)}
        worldState={isGoodWorld ? 'good' : 'bad'}
      />
    </div>
  );
});
